import { os } from "@utils/os"
import { useCallback, useEffect, useRef } from "react"
import { useDebounceCallback } from "usehooks-ts"
import { DEBOUNCE_ULTRA_SHORT_DELAY } from "./use-debounce"

type Options = {
	/* keydown will be used instead of keyup event, default false */
	keydown?: boolean
	/* defines the separator for keys combination, default "+" */
	separator?: string
	debounceDelay?: number
}
const currentOs = os()

enum SpecialKeys {
	Minus = "-",
	Plus = "+",
}

// Define key mapping for OS-specific transformations
const KEY_MAPPINGS = {
	MacOS: {
		Alt: "Control",
		Command: "Meta", // Command key is represented as Meta in JavaScript events
	},
}

/**
 * Define a comma separated list of hotkeys with either:
 * - a combination of keys (e.g.: "Alt+s")
 * - one key
 *
 * On macOS, ctrl key automatically replaces an alt key.
 * On macOS, a Command key is mapped to Meta (which is how the browser represents it).
 *
 * @example
 * useHotkeys('Alt+s,ArrowDown', useCallback((event, hotkey) => {
 *  if(hotkey === "ArrowDown") {
 *    // the arrow down key was pressed
 *  } else if (hotkey === "Alt+s") {
 *    // Alt (or ctrl on macOS) and "s" keys were pressed
 *  }
 * }), []);
 */
export const useHotkey = (
	hotkeysList: string | string[],
	callback: (event: KeyboardEvent, hotkey: string) => void,
	options: Partial<Options> = {},
) => {
	const { keydown = false, separator = "+", debounceDelay = DEBOUNCE_ULTRA_SHORT_DELAY } = options

	// Use useRef to persist these values between renders
	const keysPressedRef = useRef<Set<string>>(new Set())
	const hotkeysMapRef = useRef<Map<string, string[]>>(new Map())

	// Setup hotkeysMap once when the hook initializes or when hotkeysList changes
	useEffect(() => {
		const hotkeys = Array.isArray(hotkeysList) ? hotkeysList : hotkeysList.split(",")
		hotkeysMapRef.current.clear()
		const osMapping = KEY_MAPPINGS[currentOs as keyof typeof KEY_MAPPINGS] || {}

		for (const hotkey of hotkeys) {
			const parsedParts = hotkey.split(separator)

			// handle special hotkeys that uses the separator as a combination, like +1 or -2
			const parts = parsedParts.map((p) => {
				// Apply OS-specific key mapping if exists
				if (osMapping[p as keyof typeof osMapping]) return osMapping[p as keyof typeof osMapping]

				const special = SpecialKeys[p as keyof typeof SpecialKeys]
				if (special) return special
				return p
			})

			hotkeysMapRef.current.set(hotkey, parts)
		}
	}, [hotkeysList, separator])

	// key press is accumulated in a set, flushed at every period
	// this prevents requirement exact order of keys pressed
	// and allow just a combination set of required keys
	const debouncedTrigger = useDebounceCallback((cb) => cb(), debounceDelay)

	const triggerHotkeys = useCallback(
		(e: KeyboardEvent) => {
			debouncedTrigger(() => {
				const pressed = [...keysPressedRef.current]
				keysPressedRef.current.clear()
				const hotkeyEntries = hotkeysMapRef.current.entries()
				for (const [hotkey, parts] of hotkeyEntries) {
					const hasCombination =
						parts.length === pressed.length && parts.every((part) => pressed.some((key) => key === part))

					if (!hasCombination) continue
					callback(e, hotkey)
					break
				}
			})
		},
		[callback, debouncedTrigger],
	)

	const handleKeydown = useCallback(
		(e: KeyboardEvent) => {
			keysPressedRef.current.add(e.key)
			if (keydown) triggerHotkeys(e)
		},
		[triggerHotkeys, keydown],
	)

	const handleKeyup = useCallback(
		(e: KeyboardEvent) => {
			if (!keydown) triggerHotkeys(e)
		},
		[triggerHotkeys, keydown],
	)

	useEffect(() => {
		window.addEventListener("keydown", handleKeydown)
		window.addEventListener("keyup", handleKeyup)

		return () => {
			window.removeEventListener("keydown", handleKeydown)
			window.removeEventListener("keyup", handleKeyup)
		}
	}, [handleKeydown, handleKeyup])
}
